// AIMS E-Learning Platform API Interfaces
// Generated from OpenAPI 3.0.3 specification

// =============================================================================
// ENUMS & CONSTANTS
// =============================================================================

export enum UserRole {
    MANAGEMENT = 'management',
    AI_OFFICE = 'ai_office',
    DEVELOPER = 'developer',
    USER = 'user'
}

export type ThemeMode = 'light' | 'dark' | 'system';

export enum UserStatus {
    ACTIVE = 'active',
    INACTIVE = 'inactive',
    SUSPENDED = 'suspended',
    PENDING = 'pending'
}

export enum CourseStatus {
    DRAFT = 'draft',
    PUBLISHED = 'published',
    ARCHIVED = 'archived',
    UNDER_REVIEW = 'under_review'
}

export enum CourseCategory {
    AI_MANAGEMENT = 'ai_management',
    EU_AI_ACT = 'eu_ai_act',
    COMPLIANCE = 'compliance',
    ETHICS = 'ethics',
    TECHNICAL = 'technical',
    GENERAL = 'general'
}

export enum DifficultyLevel {
    BEGINNER = 'beginner',
    INTERMEDIATE = 'intermediate',
    ADVANCED = 'advanced'
}

export enum QuestionType {
    SINGLE = 'single',
    MULTI = 'multi',
    TRUTH = 'truth'
}

// =============================================================================
// CORE RESPONSE TYPES
// =============================================================================

export interface ApiResponse<T> {
    data?: T;
    error?: string;
    message?: string;
    details?: any;
    timestamp: string;
}

export interface PaginationResponse<T> {
    total: number;
    skip: number;
    limit: number;
    hasNext: boolean;
    hasPrev: boolean;
    data: T[];
}

export interface ErrorResponse {
    error: string;
    message?: string;
    details?: any;
    timestamp: string;
}

// =============================================================================
// SYSTEM INTERFACES
// =============================================================================

export interface ApiInfo {
    message: string;
    version: string;
    docs: string;
    health: string;
}

export interface HealthStatus {
    status: string;
    timestamp: string;
    service: string;
}

export interface KeycloakConfig {
    serverUrl: string;
    realm: string;
    clientId: string;
    authUrl: string;
    tokenUrl: string;
    logoutUrl: string;
}

// =============================================================================
// USER & AUTHENTICATION INTERFACES
// =============================================================================

export interface User {
    userId: string; // Changed from 'id' to match OpenAPI spec
    keycloakId: string;
    email: string;
    username: string; // Added - readonly in OpenAPI spec
    firstName?: string; // Readonly in OpenAPI spec
    lastName?: string; // Readonly in OpenAPI spec
    fullName?: string; // Readonly in OpenAPI spec
    roles: UserRole[];
    status: UserStatus;
    organization?: string[]; // Array of organizations
    language?: string;
    timezone?: string;
    displayUsername?: string;
    theme?: ThemeMode;
    profileImage?: string;
    useDisplayName?: boolean;
}

export interface TokenValidationRequest {
    token: string;
}

export interface TokenValidationResponse {
    valid: boolean;
    user?: User;
}

export interface UserPreferences {
    language?: string;
    theme?: ThemeMode;
}

export interface UserInfoEnhanced {
    userId: string;
    email: string;
    username: string;
    firstName?: string;
    lastName?: string;
    fullName?: string;
    roles: UserRole[];
    organization?: string[];
    twoFactorEnabled: boolean;
    preferences?: UserPreferences;
    lastLogin?: string;
    createdAt?: string;
    updatedAt?: string;
    passwordLastUpdated?: string;
}

export interface TwoFactorActivateRequest {
    redirectUri: string;
}

export interface TwoFactorActivateResponse {
    message: string;
}

export interface TwoFactorDeactivateResponse {
    message: string;
}

export interface PasswordResetRequest {
    email: string;
    redirectUri?: string;
}

export interface PasswordResetResponse {
    message: string;
}

export interface RecoveryCodesResponse {
    codes: string[];
    message: string;
}

// =============================================================================
// COURSE MANAGEMENT INTERFACES
// =============================================================================

export interface Course {
    courseId: string; // Changed from 'id' to match OpenAPI spec
    tenantId?: string; // Readonly in OpenAPI spec
    title: string;
    description: string;
    shortDescription?: string;
    category?: CourseCategory; // Made optional with default 'general'
    tags?: string[];
    status?: CourseStatus; // Made optional with default 'draft'
    language?: string; // Made optional with default 'en'
    difficultyLevel?: DifficultyLevel;
    estimatedDuration?: number; // in minutes
    coverImage?: string;
    prerequisites?: string[];
    learningObjectives?: string[];
    authorId?: string; // Readonly in OpenAPI spec
    isPublic?: boolean; // Default false
    allowSelfEnrollment?: boolean; // Default true
    certificateEnabled?: boolean; // Default true
    passingScore?: number; // 0-100
    maximumTries?: number; // Maximum attempts allowed
    targetRoles?: UserRole[]; // Roles this course targets
    modules?: Module[]; // Course content structure
    enrollmentCount?: number; // Readonly in OpenAPI spec
    completionCount?: number; // Readonly in OpenAPI spec
    averageRating?: number; // 0-5, readonly in OpenAPI spec
    createdAt?: string; // Readonly in OpenAPI spec
    updatedAt?: string; // Readonly in OpenAPI spec
    // Backward compatibility properties
    requiredRoles?: string[]; // For backward compatibility with existing components
    maxAttempts?: number; // For backward compatibility with existing components
}

export interface CourseCreate {
    title: string;
    description: string;
    shortDescription?: string;
    category?: CourseCategory;
    tags?: string[];
    language?: string;
    difficultyLevel?: DifficultyLevel;
    estimatedDuration?: number;
    coverImage?: string;
    prerequisites?: string[];
    learningObjectives?: string[];
    isPublic?: boolean;
    allowSelfEnrollment?: boolean;
    certificateEnabled?: boolean;
    passingScore?: number; // 0-100
    maximumTries?: number; // Maximum attempts allowed
    targetRoles?: string[]; // Roles this course targets
    modules?: Module[]; // Course content structure
}

export interface CourseListParams {
    skip?: number;
    limit?: number;
    search?: string;
    category?: CourseCategory;
    status?: CourseStatus;
}

// =============================================================================
// MODULE, CHAPTER & LESSON INTERFACES
// =============================================================================

export interface Module {
    moduleId?: string; // Changed from 'id' - Auto-generated UUID, readonly in OpenAPI spec
    name: string;
    description: string;
    chapters: Chapter[];
    order: number;
}

export interface Chapter {
    chapterId?: string; // Changed from 'id' - Auto-generated UUID, readonly in OpenAPI spec
    name: string;
    description: string;
    lessons: Lesson[];
    order: number;
}

export interface Lesson {
    lessonId?: string; // Changed from 'id' - Auto-generated UUID, readonly in OpenAPI spec
    name: string;
    description: string;
    type: 'text' | 'video' | 'document' | 'quiz';
    mandatory: boolean;
    order: number;
}

export interface TextLesson extends Lesson {
    type: 'text';
    content?: string;
}

export interface VideoLesson extends Lesson {
    type: 'video';
    videoUrl?: string;
}

export interface DocumentLesson extends Lesson {
    type: 'document';
    documentUrl?: string;
}

// =============================================================================
// QUIZ MANAGEMENT INTERFACES
// =============================================================================

export interface QuizAnswer {
    text: string;
    isCorrect: boolean;
    points?: number;
    selectedOptions?: string[];
}

export interface QuizQuestion {
    questionId?: string; // Changed from 'id' - UUID format, readonly in OpenAPI spec
    question: string;
    type: QuestionType;
    answers: QuizAnswer[];
    explanation?: string;
    points?: number; // Default 1 in OpenAPI spec
}

export interface Quiz {
    quizId: string; // Changed from 'id' to match OpenAPI spec
    tenantId?: string; // Readonly in OpenAPI spec
    title: string;
    description: string;
    questions: QuizQuestion[];
    timeLimit?: number; // in minutes
    attemptsAllowed?: number; // Default 3 in OpenAPI spec
    passingScore?: number; // 0-100
    randomizeQuestions?: boolean; // Default false in OpenAPI spec
    randomizeAnswers?: boolean; // Default true in OpenAPI spec
    showResults?: boolean; // Default true in OpenAPI spec
    createdAt?: string; // Readonly in OpenAPI spec
    updatedAt?: string; // Readonly in OpenAPI spec
}

export interface QuizCreate {
    title: string;
    description: string;
    questions: QuizQuestion[];
    timeLimit?: number; // in minutes
    attemptsAllowed?: number;
    passingScore?: number; // 0-100
    randomizeQuestions?: boolean;
    randomizeAnswers?: boolean;
    showResults?: boolean;
}

export interface QuizListParams {
    skip?: number;
    limit?: number;
}

// =============================================================================
// PROGRESS & ANALYTICS INTERFACES
// =============================================================================

export interface ProgressData {
    userId: string;
    courseId: string;
    progressPercentage: number; // 0-100
    completedLessons: string[];
    quizScores: { [quizId: string]: number };
    lastAccessed: string;
}

export interface Certificate {
    certificateId: string; // Changed from 'id' to match OpenAPI spec
    courseId: string;
    courseTitle: string;
    completionDate: string;
    certificateUrl: string;
    grade: number;
}

export interface AnalyticsData {
    enrollmentCount: number;
    completionRate: number;
    averageScore: number;
    engagementMetrics: {
        [key: string]: any;
    };
}

// =============================================================================
// QUERY PARAMETER BUILDERS
// =============================================================================

export interface BaseQueryParams {
    skip?: number;
    limit?: number;
}

export interface SearchableParams extends BaseQueryParams {
    search?: string;
}

export interface FilterableParams extends SearchableParams {
    [key: string]: any;
}

// =============================================================================
// HTTP CLIENT CONFIGURATION
// =============================================================================

export interface ApiConfig {
    baseUrl: string;
    timeout?: number;
    retries?: number;
    tenantId?: string;
}

export interface RequestHeaders {
    'Content-Type'?: string;
    'Authorization'?: string;
    'X-Tenant-ID'?: string;

    [key: string]: string | undefined;
}

// =============================================================================
// COURSE ENROLLMENT & STRUCTURE INTERFACES
// =============================================================================

export enum EnrollmentStatus {
    ACTIVE = 'active',
    COMPLETED = 'completed',
    SUSPENDED = 'suspended'
}

export interface CourseEnrollment {
    enrollmentId: string; // Changed from 'id' to match OpenAPI spec
    courseId: string;
    userId: string;
    tenantId?: string; // Readonly in OpenAPI spec
    enrolledAt?: string; // Readonly in OpenAPI spec
    status?: EnrollmentStatus; // Default 'active' in OpenAPI spec
}

export interface CourseWithProgress {
    courseId: string; // Changed from 'id' to match OpenAPI spec
    tenantId?: string; // Made optional, readonly in OpenAPI spec
    title: string;
    description: string;
    shortDescription?: string;
    category?: CourseCategory;
    tags?: string[];
    status?: CourseStatus; // Made optional
    language?: string; // Made optional
    difficultyLevel?: DifficultyLevel;
    estimatedDuration?: number;
    coverImage?: string;
    prerequisites?: string[];
    learningObjectives?: string[];
    authorId?: string; // Made optional, readonly in OpenAPI spec
    isPublic?: boolean; // Made optional
    allowSelfEnrollment?: boolean; // Made optional
    certificateEnabled?: boolean; // Made optional
    passingScore?: number;
    maximumTries?: number;
    targetRoles?: UserRole[];
    modules?: Module[]; // Made optional
    enrollmentCount?: number;
    completionCount?: number;
    averageRating?: number;
    createdAt?: string;
    updatedAt?: string;
    enrollment: {
        enrolled_at: string;
        status: 'active' | 'suspended';
    };
    progress: ProgressData;
}

export interface AssignedCoursesResponse {
    data: CourseWithProgress[];
    total_count: number;
    skip: number;
    limit: number;
}

export interface CourseStructure {
    courseId: string;
    title: string;
    modules: ModuleStructure[];
    totalLessons: number;
    userProgress: ProgressData;
}

export interface ModuleStructure {
    moduleId: string; // Changed from 'id' to match OpenAPI spec
    name: string;
    description: string;
    order: number;
    chapters: ChapterStructure[];
}

export interface ChapterStructure {
    chapterId: string; // Changed from 'id' to match OpenAPI spec
    name: string;
    description: string;
    order: number;
    lessons: LessonStructure[];
}

export interface LessonStructure {
    lessonId: string; // Changed from 'id' to match OpenAPI spec
    name: string;
    description: string;
    type: 'text' | 'video' | 'document'; // Removed 'quiz' as per OpenAPI CourseStructure spec
    mandatory: boolean;
    order: number;
    completed: boolean; // Whether the current user has completed this lesson
}

export interface QuizLesson extends Lesson {
    type: 'quiz';
    quizId: string;
}

export interface LessonCompletionRequest {
    timeSpent?: number;
    metadata?: { [key: string]: any };
}

export interface LessonCompletionResponse {
    lessonId: string;
    courseId: string;
    completedAt: string;
    progressPercentage: number;
    message: string;
}

export interface ProgressSummary {
    courses: ProgressData[];
    totalCourses: number;
    completedCourses: number;
    overallProgress: number;
}

// =============================================================================
// QUIZ ATTEMPT INTERFACES
// =============================================================================

export enum QuizAttemptStatus {
    IN_PROGRESS = 'in_progress',
    COMPLETED = 'completed',
    ABANDONED = 'abandoned'
}

export interface QuizAttempt {
    attemptId: string; // Changed from 'id' to match OpenAPI spec
    userId?: string; // Readonly in OpenAPI spec
    quizId: string;
    lessonId?: string;
    courseId?: string;
    attemptNumber?: number;
    questionIds?: string[];
    currentPosition?: number;
    answers?: { [questionId: string]: any };
    score?: number;
    isPassed?: boolean; // Added - new field in OpenAPI spec
    status?: QuizAttemptStatus;
    startedAt?: string;
    completedAt?: string;
    timeSpent?: number;
}

export interface QuizAnswerSubmission {
    selectedAnswers: string[];
}

export interface QuizAnswerFeedback {
    correct: boolean;
    correctAnswers: string[];
    explanation?: string;
    pointsEarned: number;
    totalPoints: number;
}

export interface QuizNextInfo {
    hasNext: boolean;
    nextQuestionId?: string;
    completed: boolean;
    progress: {
        current: number;
        total: number;
    };
}

export interface QuizResults {
    attemptId: string;
    score: number;
    passed: boolean;
    totalQuestions: number;
    correctAnswers: number;
    timeSpent: number;
    completedAt: string;
    passingScore: number;
    percentage: number;
    performanceLevel: 'excellent' | 'good' | 'satisfactory' | 'needs_improvement';
    recommendations?: string[];
    strengths?: string[];
    weaknesses?: string[];
    canRetake: boolean;
    attemptsRemaining?: number;
}

// =============================================================================
// USER MANAGEMENT INTERFACES
// =============================================================================

export interface UserCreate {
    email: string;
    first_name: string;
    last_name: string;
    password: string;
    roles: string[];
    profile_data?: any;
}

export interface UserCreateRequest {
    email: string;
    username: string;
    firstName: string;
    lastName: string;
    roles: string[];
    organization?: string[];
    password: string;
}

export interface UserUpdate {
    first_name?: string;
    last_name?: string;
    roles?: string[];
    status?: string;
    profile_data?: any;
}

export interface UserInvite {
    email: string;
    first_name: string;
    last_name: string;
    roles: string[];
    message?: string;
}

export interface OrganizationDomain {
    name: string;
    verified: boolean;
}

export interface UserListResponse {
    users: UserListItem[];
    total: number;
    domains: OrganizationDomain[];
}

export interface UserListItem {
    userId: string;
    username: string;
    firstName: string;
    lastName: string;
    fullName: string;
    email: string;
    roles: string[];
    organization: string[];
    status: string;
    profileImage?: string;
}

// =============================================================================
// ENHANCED COURSE PROGRESS INTERFACES
// =============================================================================

export interface ModuleProgress {
    id: string;
    module_id: string;
    chapters: ChapterProgress[];
    completion_percentage: number;
    completed: boolean;
    time_spent_minutes: number;
    started_at?: string;
    completed_at?: string;
}

export interface ChapterProgress {
    id: string;
    chapter_id: string;
    lessons: LessonProgress[];
    completion_percentage: number;
    completed: boolean;
    time_spent_minutes: number;
    started_at?: string;
    completed_at?: string;
}

export interface LessonProgress {
    id: string;
    lesson_id: string;
    completed: boolean;
    score?: number;
    time_spent_minutes: number;
    attempts?: number;
    started_at?: string;
    completed_at?: string;
}

export interface CourseProgress {
    id: string;
    user_id: string;
    course_id: string;
    tenant_id: string;
    modules: ModuleProgress[];
    completion_percentage: number;
    completed: boolean;
    time_spent_minutes: number;
    started_at: string;
    last_accessed: string;
    completed_at?: string;
}

// =============================================================================
// REPORT MANAGEMENT INTERFACES
// =============================================================================

export interface ReportExportResponse {
    message: string;
    filename: string;
    data?: any;
}

export interface ReportFilter {
    course_id?: string;
    start_date?: string;
    end_date?: string;
    user_role?: string;
    status?: string;
}

export interface UserProgressReport {
    user_id: string;
    user_name: string;
    user_email: string;
    course_id: string;
    course_title: string;
    enrollment_date: string;
    completion_date?: string;
    progress_percentage: number;
    time_spent_minutes: number;
    quiz_scores: { [quiz_id: string]: number };
    status: 'not_started' | 'in_progress' | 'completed';
}

export interface CourseAnalyticsReport {
    course_id: string;
    course_title: string;
    total_enrollments: number;
    active_learners: number;
    completion_rate: number;
    average_completion_time: number;
    average_score: number;
    engagement_metrics: {
        daily_active_users: number;
        session_duration_avg: number;
        bounce_rate: number;
    };
    popular_lessons: Array<{
        lesson_id: string;
        lesson_title: string;
        views: number;
        completion_rate: number;
    }>;
}

export interface DashboardMetrics {
    totalUsers: number;
    activeCourses: number;
    overallEngagementScore: number;
    completionRate: number; // Average course completion rate percentage (0-100%)
    userCertificates: number;
    recentActivities: RecentActivity[];
}

export interface RecentActivity {
    user: string;
    action: string;
    course: string;
    timestamp: string; // ISO 8601 format (e.g., "2025-08-28T08:20:31.930Z")
}

// =============================================================================
// UTILITY TYPES
// =============================================================================

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

export interface HttpOptions {
    headers?: RequestHeaders;
    params?: { [key: string]: string | number | boolean };
    observe?: 'body' | 'response';
    responseType?: 'json' | 'text' | 'blob';
}

export interface GeneralCoursesReport {
    totalUsers: number;
    averageQuizScore: number;
    courseCompletionData: CourseCompletionData[];
}

// Alternative interface for when getReport() returns ProgressData array directly
export type GeneralCoursesReportResponse = GeneralCoursesReport | ProgressData[];

export interface CourseCompletionData {
    courseId: string;
    courseTitle: string;
    enrolledCount: number;
    completedCount: number;
    completionRate: number;
}

// User-specific progress summary for reports
export interface UserReportSummary {
    user: UserListItem;
    totalCoursesAssigned: number;
    coursesCompleted: number;
    coursesFailed: number;
    coursesInProgress: number;
    successRate: number; // percentage of completed courses
    averageScore?: number;
    totalTimeSpent?: number; // in minutes
    courseDetails: UserCourseDetail[];
}

export interface UserCourseDetail {
    courseId: string;
    courseTitle: string;
    status: 'not_started' | 'in_progress' | 'completed' | 'failed';
    completionPercentage: number;
    score?: number;
    timeSpent?: number; // in minutes
    lastAccessed?: string;
}